import { Core } from "@unseenco/taxi";
import { Dom } from "./dom";
import hey from "./hey";
import { Scroll } from "./scroll";
import gsap, { easeMenu } from "./gsap";

/**
 * Default Page Transition
 *
 * Standard transition with fade/slide effect.
 * Old page transitions out, gets removed, then new page transitions in.
 *
 * Lifecycle:
 * 1. onLeave: Modules transitionOut → scroll to top → remove old page
 * 2. onEnter: Modules transitionIn → start modules → cleanup
 */
class Transition {
  constructor({ wrapper }) {
    this.wrapper = wrapper;
  }

  leave(props) {
    return new Promise((resolve) => {
      this.onLeave({ ...props, done: resolve });
    });
  }

  enter(props) {
    return new Promise((resolve) => {
      this.onEnter({ ...props, done: resolve });
    });
  }

  async onLeave({ from, done }) {
    hey.PAGE_OUT = from;

    // Stop scroll during transition
    Scroll.stop();

    // Let modules animate out and cleanup
    await Dom.transitionOut();

    // Scroll to top before showing new page
    Scroll.toTop();

    // Remove old page
    from.remove();

    done();
  }

  async onEnter({ to, done }) {
    // Create and start new modules
    await Dom.transitionIn();

    // Update state
    hey.PAGE_SLUG = window.location.pathname;
    hey.PAGE_IN = to;

    // Resume scroll
    Scroll.start();
    Scroll.resize();

    done();

    // Cleanup wrapper styles after transition settles
    setTimeout(() => {
      this.wrapper.removeAttribute("style");
    }, 100);
  }
}

class CoverTransition {
    constructor({ wrapper }) {
        this.wrapper = wrapper;
        this.cover = document.querySelector("[data-transition-cover] > *");
    }

    leave(props) {
        return new Promise((resolve) => {
            this.onLeave({ ...props, done: resolve });
        });
    }

    enter(props) {
        return new Promise((resolve) => {
            this.onEnter({ ...props, done: resolve });
        });
    }

    async onLeave({ from, done }) {
        hey.PAGE_OUT = from;

        await Dom.transitionOut();

        await gsap.fromTo(this.cover,
            { clipPath: "inset(100% 0 0 0)" }, // Hidden clipped from top
            {
                clipPath: "inset(0% 0 0 0)", // Fully visible
                duration: 0.6,
                ease: "power3.inOut",
            }
        );

        Scroll.toTop();
        from.remove();

        done();
    }

    async onEnter({ to, done }) {

        // Or replace Dom.create() and Dom.start() with `await Dom.transitionIn()` for quicker module transitions
        Dom.create();

        await new Promise((resolve) => {
            const tl = gsap.timeline({
                onComplete: () => {
                    resolve();
                }
            });

            // Animate cover from top to bottom
            tl.to(this.cover, {
                clipPath: "inset(0 0 100% 0)", // Clipped from bottom,
                duration: 0.6,
                ease: "power3.inOut"
            });

            // Start DOM animations 0.4s before cover transition completes
            // This creates a smoother transition where page animations begin
            // just as the cover is finishing its exit
            tl.call(() => {
                Dom.start();
            }, null, "-=0.6");
        });

        // Reset for next transition
        gsap.set(this.cover, { clipPath: "inset(100% 0 0 0)" })

        hey.PAGE_SLUG = window.location.pathname;
        hey.PAGE_IN = to;

        Scroll.start();
        Scroll.resize();

        done();

        this.wrapper.removeAttribute("style");
    }
}

/**
 * Loop Transition
 *
 * Creative transition where the new page slides up over the old page.
 * Old page slides up and out while new page is revealed.
 *
 * Use: <a href="/page" data-taxi-transition="loop">Link</a>
 */
class LoopTransition {
  constructor({ wrapper }) {
    this.wrapper = wrapper;
    this.oldPage = null;
  }

  leave(props) {
    return new Promise((resolve) => {
      this.onLeave({ ...props, done: resolve });
    });
  }

  enter(props) {
    return new Promise((resolve) => {
      this.onEnter({ ...props, done: resolve });
    });
  }

  async onLeave({ from, done }) {
    hey.PAGE_OUT = from;
    this.oldPage = from;

    // Stop scroll during transition
    Scroll.stop();

    // Let modules animate out
    await Dom.transitionOut();

    done();
  }

  #setupOldPage(to) {
    // Position new page below viewport
    gsap.set(to, {
      y: window.innerHeight,
    });

    this.wrapper.removeAttribute("style");

    // Fix old page in place
    if (this.oldPage) {
      this.oldPage.style.position = "fixed";
      this.oldPage.style.width = "100%";
      this.oldPage.style.bottom = "0";
      this.oldPage.style.zIndex = "5";
    }

    // Reset scroll position
    Scroll.toTop();

    // Animate old page up and out
    gsap.to(this.oldPage, {
      y: -window.innerHeight,
      duration: 1.2,
      ease: easeMenu,
    });
  }

  async onEnter({ to, done }) {
    this.#setupOldPage(to);

    // Start creating new modules while animating
    Dom.transitionIn();

    // Animate new page up into view
    await gsap.to(to, {
      y: 0,
      duration: 1.2,
      ease: easeMenu,
    });

    // Cleanup old page
    this.oldPage?.remove();
    this.oldPage = null;

    // Resume scroll
    Scroll.start();
    Scroll.resize();

    // Update state
    hey.PAGE_SLUG = window.location.pathname;
    hey.PAGE_IN = to;

    done();
  }
}

/**
 * Fade Transition
 *
 * Simple crossfade between pages.
 *
 * Use: <a href="/page" data-taxi-transition="fade">Link</a>
 */
class FadeTransition {
  constructor({ wrapper }) {
    this.wrapper = wrapper;
  }

  leave(props) {
    return new Promise((resolve) => {
      this.onLeave({ ...props, done: resolve });
    });
  }

  enter(props) {
    return new Promise((resolve) => {
      this.onEnter({ ...props, done: resolve });
    });
  }

  async onLeave({ from, done }) {
    hey.PAGE_OUT = from;

    Scroll.stop();

    await Dom.transitionOut();

    // Fade out old page
    await gsap.to(from, {
      opacity: 0,
      duration: 0.5,
      ease: "power2.out",
    });

    Scroll.toTop();
    from.remove();

    done();
  }

  async onEnter({ to, done }) {
    // Start invisible
    gsap.set(to, { opacity: 0 });

    await Dom.transitionIn();

    // Fade in new page
    await gsap.to(to, {
      opacity: 1,
      duration: 0.5,
      ease: "power2.out",
    });

    hey.PAGE_SLUG = window.location.pathname;
    hey.PAGE_IN = to;

    Scroll.start();
    Scroll.resize();

    done();

    this.wrapper.removeAttribute("style");
  }
}

/**
 * Pages Manager
 *
 * Extends Taxi.js Core to handle page transitions.
 * Manages module lifecycle during navigation.
 *
 * Available transitions:
 * - default: Standard transition
 * - loop: Slide-up reveal
 * - fade: Crossfade
 *
 * Add custom transitions by extending this class or
 * adding new transition classes to the transitions object.
 */
class _Pages extends Core {
  constructor() {
    super({
      links: "a:not([target]):not([href^=\\#]):not([data-taxi-ignore])",
      removeOldContent: false,
      allowInterruption: false,
      bypassCache: true,
      transitions: {
        default: CoverTransition,
        loop: LoopTransition,
        fade: FadeTransition,
      },
    });

    // Set initial page slug
    hey.PAGE_SLUG = window.location.pathname;

    console.log("🚕 Taxi ready");
  }
}

// Export singleton
export const Pages = new _Pages();
